import {
  KdsCardFile,
  KdsDropfile,
  KdsText,
  KdsTextField,
} from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { isAxiosError } from "axios";
import { useAtomValue } from "jotai";
import {
  type KeyboardEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FileContext } from "@/components/FileUploadAsync/FileUploadAsync.context";
import { SIZE_5G } from "@/constants/fileSize.constants";
import { useDirectUpload } from "@/hooks";
import { type InferredOrderDetailsAddQuoteFormPageSchema } from "@/pages/OrderDetailsAddQuoteFormPage/OrderDetailsAddQuoteFormPage.schema";
import { currentWorkspaceAtom } from "@/store";
import { type GetQuoteStepProps } from "../ModalAddQuote.types";

export const AddQuoteQuoteStep = ({
  fileFromAws,
  goNextStep,
}: GetQuoteStepProps) => {
  const { t } = useTranslate();
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);
  const methods = useFormContext<InferredOrderDetailsAddQuoteFormPageSchema>();

  const [isLoading, setIsLoading] = useState(false);

  const fileName = fileFromAws?.name;
  const fileSize = fileFromAws?.size;

  const fileUrl = useMemo(() => {
    if (fileFromAws) {
      return URL.createObjectURL(fileFromAws);
    }
    return undefined;
  }, [fileFromAws]);

  useEffect(() => {
    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [fileUrl]);

  const { pushFile, setFileUploadState } = useContext(FileContext);
  const { uploadFile, createFileUploadUrl } = useDirectUpload();

  const handleUpload = async (file: File) => {
    try {
      const uploadDetails = await createFileUploadUrl(
        "orderItem.deliveryNotes",
        file
      );
      const newSignedId = uploadDetails?.fileSignedId;

      pushFile(newSignedId, {
        fileName: file.name,
        size: file.size,
        contentType: file.type,
      });

      await uploadFile(file, uploadDetails);

      setFileUploadState(newSignedId, "uploaded");

      methods.setValue("fileSignedId", newSignedId);
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 422) {
          setFileUploadState(error.response?.data.fileSignedId, "error");
          methods.setError("fileSignedId", {
            type: "manual",
            message: t("dropfile.errorDropfileType"),
          });
          methods.setValue("fileSignedId", "");
          setIsLoading(false);
        }
      }
    }
  };

  // Handle Go Next Step on Enter key Press
  const onEnterPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      return goNextStep();
    }
  };

  return (
    <div className="flex w-full flex-col gap-6 p-6 px-8">
      <KdsText size="subtitle-md">
        {fileFromAws
          ? t("oms.addQuote.stepper.quoteStepSubtitleFromAws")
          : t("oms.addQuote.stepper.quoteStepSubtitle")}{" "}
      </KdsText>

      <div className="flex flex-col-reverse gap-8">
        <div className="flex flex-col gap-4">
          <div className="flex flex-row flex-wrap items-center gap-4 md:flex-nowrap">
            <KdsTextField
              label={t("inputs.totalPriceHT.label")}
              placeholder={`0,00 ${currentWorkspace.currency}`}
              type="number"
              disabled={!!fileFromAws}
              min={0}
              step="0.5"
              onKeyDown={onEnterPress}
              hasError={!!methods.formState.errors.totalPrice}
              errorMessage={t("inputs.requiredField")}
              dataCy="quotePrice"
              errorDataCy="quotePriceError"
              {...methods.register("totalPrice", {
                valueAsNumber: true,
                required: true,
              })}
            />
          </div>

          <Controller
            name="fileSignedId"
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              if (fileFromAws) {
                return (
                  <>
                    {fileName && fileSize && (
                      <KdsCardFile
                        isViewable
                        isDownloadable
                        fileName={fileName}
                        fileSize={fileSize}
                        fileUrl={fileUrl}
                      />
                    )}
                  </>
                );
              }

              return (
                <div className="flex flex-col gap-sp-xs2">
                  <KdsText>{t("inputs.quoteHdFile.label")}</KdsText>
                  <KdsDropfile
                    dataCy="fileUploadForQuote"
                    errorMessage={t("inputs.requiredField")}
                    hasError={!!fieldState.error}
                    isDeletable={!!field.value}
                    isReadable={!!field.value}
                    isLoading={isLoading}
                    maxSize={SIZE_5G}
                    onDelete={() => field.onChange(undefined)}
                    onUpload={(value) => void handleUpload(value)}
                    onUploadError={() => methods.setValue("fileSignedId", "")}
                    onUploadStart={() => {
                      setIsLoading(true);
                      methods.clearErrors("fileSignedId");
                    }}
                    onUploadSuccess={() => setIsLoading(false)}
                    placeholderDescription={t("dropfile.placeholderMaxSize5GB")}
                    placeholderTitle={t("dropfile.placeholderTitle")}
                  />
                </div>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};
